<template>
    <div id="app" class="h-100" :class="[skinClasses]">
        <component :is="layout">
            <router-view />
        </component>
        <ManagerNotification />
    </div>
</template>

<script>
import { watch } from '@vue/composition-api'
import { useWindowSize, useCssVar } from '@vueuse/core'
import { provideToast } from 'vue-toastification/composition'

import { useAuth } from '@/composables/useAuth'
import { useCentrifugo } from '@/composables/useCentrifugo'
import { usePlayNotifications } from '@/composables/usePlayNotifications'
import { usePrintNotifications } from '@/composables/usePrintNotifications'
import { useUserNotifications } from '@/composables/useUserNotifications'
import useAppConfig from '@/core/app-config/useAppConfig'
import ManagerNotification from '@/core/components/toastification/ManagerNotification.vue'
import { useDispatcherNotifications } from '@/modules/dispatcher/composables/useDispatcherNotifications'
import { useStopListNotifications } from '@/modules/menu-management/composables/useStopListNotifications'
import { useShiftNotifications } from '@/modules/shift-and-reports/composables/useShiftNotifications'
import store from '@/store'
import { $themeColors, $themeBreakpoints, $themeConfig } from '@themeConfig'

const LayoutHorizontal = () =>
    import('@/layouts/horizontal/LayoutHorizontal.vue')
const LayoutFull = () => import('@/layouts/full/LayoutFull.vue')

const { isAuthenticated } = useAuth()
const { disconnect: disconnectCentrifugo } = useCentrifugo()
const { initStopListNotifications } = useStopListNotifications()
const { initShiftNotifications } = useShiftNotifications()
const { initUserNotifications, resetUserNotifications } = useUserNotifications()
const { initPrintNotifications, resetPrintNotifications } =
    usePrintNotifications()
const { initPlayNotifications, resetPlayNotifications } = usePlayNotifications()
const { initDispatcherNotifications } = useDispatcherNotifications()

export default {
    components: {
        ManagerNotification,
        // Layouts
        LayoutHorizontal,
        LayoutFull,
    },
    setup() {
        const { skin, skinClasses } = useAppConfig()
        // If skin is dark when initialized => Add class to body
        if (skin.value === 'dark') document.body.classList.add('dark-layout')

        // Set Window Width in store
        store.commit('app/UPDATE_WINDOW_WIDTH', window.innerWidth)
        const { width: windowWidth } = useWindowSize()
        watch(windowWidth, val => {
            store.commit('app/UPDATE_WINDOW_WIDTH', val)
        })

        provideToast({
            hideProgressBar: true,
            closeOnClick: false,
            closeButton: false,
            icon: false,
            timeout: 3000,
            transition: 'Vue-Toastification__fade',
        })

        watch(
            isAuthenticated,
            isNowAuthenticated => {
                if (isNowAuthenticated) {
                    initStopListNotifications()
                    initShiftNotifications()
                    initUserNotifications()
                    initPrintNotifications()
                    initPlayNotifications()
                    initDispatcherNotifications(
                        'eds:orders:division',
                        'eds:routes'
                    )
                } else {
                    resetUserNotifications()
                    resetPrintNotifications()
                    resetPlayNotifications()
                    disconnectCentrifugo()
                }
            },
            {
                immediate: true,
            }
        )

        return {
            skinClasses,
        }
    },

    computed: {
        layout() {
            if (
                this.$route &&
                this.$route.meta &&
                this.$route.meta.layout === 'full'
            )
                return 'layout-full'
            return `layout-horizontal`
        },
    },
    beforeCreate() {
        // Set colors in theme
        const colors = [
            'primary',
            'secondary',
            'success',
            'info',
            'warning',
            'danger',
            'light',
            'dark',
        ]

        // eslint-disable-next-line no-plusplus
        for (let i = 0, len = colors.length; i < len; i++) {
            $themeColors[colors[i]] = useCssVar(
                `--${colors[i]}`,
                document.documentElement
            ).value.trim()
        }

        // Set Theme Breakpoints
        const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl']

        // eslint-disable-next-line no-plusplus
        for (let i = 0, len = breakpoints.length; i < len; i++) {
            $themeBreakpoints[breakpoints[i]] = Number(
                useCssVar(
                    `--breakpoint-${breakpoints[i]}`,
                    document.documentElement
                ).value.slice(0, -2)
            )
        }

        // Set RTL
        const { isRTL } = $themeConfig.layout
        document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr')
    },
}
</script>

<style lang="scss">
@import 'src/core/scss/core.scss';
@import 'src/assets/scss/style.scss';
@import 'vue-select/dist/vue-select.css';
@import 'src/core/scss/vue/libs/vue-select.scss';
</style>
